/**
 * Marketing-tracking capture for the public booking widget.
 *
 * Every scheduler link placement is tagged with unique utm params
 * (one utm_source per placement). The qualify flow must carry those
 * from the landing URL through /api/qualify and /api/book so the
 * routing audit log, the bookings row, the Zapier webhook, and the
 * Appointment-FYI email can attribute the booking. QualifyRequest /
 * BookRequest have carried optional utm_* / kuid / kref fields since
 * PR #4 ("captured from URL params on widget mount"), but the widget
 * never actually captured them — this module is that missing piece.
 *
 * Semantics:
 * - Any page view whose URL carries at least one tracking param
 *   persists the full set to sessionStorage, so the values survive
 *   in-app phase changes and a refresh that drops the query string.
 * - A later URL with params overwrites (most recent click wins
 *   within the tab).
 * - Reads never throw: private-mode storage failures and exotic
 *   environments degrade to "no tracking", never a broken form.
 */

export interface TrackingParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  kuid?: string;
  kref?: string;
}

const KEYS: ReadonlyArray<keyof TrackingParams> = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'kuid',
  'kref',
];

const STORAGE_KEY = 'aa_tracking_params';

function readFromUrl(): TrackingParams {
  const out: TrackingParams = {};
  try {
    const qs = new URLSearchParams(window.location.search);
    for (const k of KEYS) {
      const v = qs.get(k);
      if (v && v.trim()) out[k] = v.trim().slice(0, 255);
    }
  } catch {
    // No window / malformed URL — treat as untracked.
  }
  return out;
}

/**
 * Returns the tracking params for this visit. Call once on widget
 * mount (to capture + persist while the URL still has the params)
 * and again at submit time (to read them back).
 */
export function getTrackingParams(): TrackingParams {
  const fromUrl = readFromUrl();
  if (Object.keys(fromUrl).length > 0) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromUrl));
    } catch {
      // Storage unavailable (private mode) — URL values still returned.
    }
    return fromUrl;
  }
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as TrackingParams;
  } catch {
    // Corrupt/unavailable storage — fall through to empty.
  }
  return {};
}
